import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/ui";
import { useAuth } from "@/hooks/auth";
import { useParams } from "react-router-dom";

interface AttendanceRecord {
  id: string;
  school_id: string;
  student_id: string;
  date: string;
  period?: number;
  status: "present" | "absent" | "late" | "excused" | "absent_with_reason";
  notes?: string;
  recorded_by?: string;
  recorded_at: string;
}

async function fetchAttendance({
  schoolId,
  date,
  period,
}: {
  schoolId: string;
  date: string;
  period?: number;
}) {
  let query = supabase
    .from("attendance_records")
    .select("*")
    .eq("school_id", schoolId)
    .eq("date", date);

  if (period !== undefined) {
    query = query.eq("period", period);
  }

  const { data, error } = await query;

  if (error) throw error;

  return (data || []).map((record) => ({
    ...record,
    status: record.status as
      | "present"
      | "absent"
      | "late"
      | "excused"
      | "absent_with_reason",
  }));
}

export const useRealAttendance = () => {
  const { toast } = useToast();
  const { user, userProfile: profile } = useAuth();
  const { schoolId: schoolIdParam } = useParams();
  const schoolId = schoolIdParam || profile?.school_id || user?.id;
  const queryClient = useQueryClient();

  // -------------------------------
  // 🟩 Fetch attendance by date
  // -------------------------------
  const useAttendanceByDate = (date?: string, period?: number) =>
    useQuery({
      queryKey: ["attendance", schoolId, date, period],
      queryFn: () => fetchAttendance({ schoolId, date: date!, period }),
      enabled: !!date && !!schoolId, // only run if date & schoolId available
      staleTime: 1000 * 60, // 1 min cache
      retry: 1,
      meta: {
        onError: (error: Error) => {
          toast({
            title: "خطأ في تحميل البيانات",
            description: error.message,
            variant: "destructive",
          });
        },
      },
    });

  // -------------------------------
  // 🟦 Record single attendance
  // -------------------------------
  const recordAttendance = useMutation({
    mutationFn: async ({
      studentId,
      date,
      status,
      period,
      notes,
    }: {
      studentId: string;
      date: string;
      status: "present" | "absent" | "late" | "excused" | "absent_with_reason";
      period?: number;
      notes?: string;
    }) => {
      if (!user) throw new Error("User not authenticated");

      const recordData = {
        school_id: schoolId,
        student_id: studentId,
        date,
        status,
        period: period || 1,
        notes,
        recorded_by: user.id,
      };

      const { data, error } = await supabase
        .from("attendance_records")
        .upsert(recordData, {
          onConflict: "school_id,student_id,date,period",
        })
        .select()
        .single();

      if (error) throw error;

      return {
        ...data,
        status: data.status as
          | "present"
          | "absent"
          | "late"
          | "excused"
          | "absent_with_reason",
      };
    },
    onSuccess: (newRecord) => {
      // update the cache for this date/period
      queryClient.setQueryData<AttendanceRecord[]>(
        ["attendance", schoolId, newRecord.date, newRecord.period],
        (old = []) => {
          const existingIndex = old.findIndex(
            (r) =>
              r.student_id === newRecord.student_id &&
              r.date === newRecord.date &&
              r.period === newRecord.period
          );
          if (existingIndex >= 0) {
            const updated = [...old];
            updated[existingIndex] = newRecord;
            return updated;
          }
          return [...old, newRecord];
        }
      );
    },
    onError: () => {
      toast({
        title: "خطأ في تسجيل الحضور",
        description: "لم نتمكن من تسجيل الحضور",
        variant: "destructive",
      });
    },
  });

  // -------------------------------
  // 🟨 Record bulk attendance
  // -------------------------------
  const recordBulkAttendance = useMutation({
    mutationFn: async (
      attendanceData: Array<{
        studentId: string;
        date: string;
        status:
          | "present"
          | "absent"
          | "late"
          | "excused"
          | "absent_with_reason";
        period?: number;
        absent_reason?: string;
      }>
    ) => {
      if (!user) throw new Error("User not authenticated");

      const records = attendanceData.map((item) => ({
        school_id: schoolId,
        student_id: item.studentId,
        date: item.date,
        status: item.status,
        period: item.period || 1,
        recorded_by: user.id,
        absent_reason: item.absent_reason,
      }));

      const { data, error } = await supabase
        .from("attendance_records")
        .upsert(records, {
          onConflict: "school_id,student_id,date,period",
        })
        .select();

      if (error) throw error;

      return (data || []).map((record) => ({
        ...record,
        status: record.status as
          | "present"
          | "absent"
          | "late"
          | "excused"
          | "absent_with_reason",
      }));
    },
    onSuccess: (newRecords) => {
      // merge new records into cache
      newRecords.forEach((record) => {
        queryClient.setQueryData<AttendanceRecord[]>(
          ["attendance", schoolId, record.date, record.period],
          (old = []) => {
            const filtered = old.filter(
              (r) =>
                !(
                  r.student_id === record.student_id &&
                  r.date === record.date 
                  // r.period === record.period
                )
            );
            return [...filtered, record];
          }
        );
      });
    },
    onError: () => {
      toast({
        title: "خطأ في تسجيل الحضور",
        description: "لم نتمكن من تسجيل الحضور الجماعي",
        variant: "destructive",
      });
    },
  });

  // -------------------------------
  // 🟧 Get single student's attendance (from cache)
  // -------------------------------
  const getStudentAttendance = (
    studentId: string,
    date: string,
    period?: number
  ): AttendanceRecord | undefined => {
    const cacheKeys = queryClient
      .getQueryCache()
      .findAll({ queryKey: ["attendance", schoolId, date, period] });
    const cachedData =
      cacheKeys
        .map((entry) => entry.state.data as AttendanceRecord[])
        .flat()
        .find(
          (record) =>
            record?.student_id === studentId &&
            record?.date === date &&
            (record?.period === period || record.period === 1)
        ) ?? undefined;

    return cachedData;
  };

  return {
    useAttendanceByDate, // hook for fetching attendance
    recordAttendance: recordAttendance.mutateAsync,
    recordBulkAttendance: recordBulkAttendance.mutateAsync,
    getStudentAttendance,
  };
};
